/**
 * Map `?origin=` selection → Season Summary origin key (Ask #55).
 * Packs are keyed by scope: bare dest = Peru∪Chile (BOTH), `__PERU` / `__CHILE`
 * suffix = a single origin. Status / Importer filters never reach this.
 */
import { countryKey } from './countryFilters.js';

/** @typedef {{ mode: 'all'|'some'|'none', keys: Set<string> }} OriginSel */

const SEASON_ORIGINS = new Set(['PERU', 'CHILE']);

/**
 * @param {OriginSel|null|undefined} originSel
 * @returns {{
 *   key: string,
 *   codes: string[],
 *   supported: boolean,
 *   scopeSuffix: ''|'__PERU'|'__CHILE',
 * }}
 *   key: ALL | BOTH | PERU | CHILE | NONE
 */
export function resolveSeasonOriginKey(originSel) {
  if (!originSel || originSel.mode === 'all') {
    // All origins on the map → Peru∪Chile season program (BOTH pack).
    return { key: 'ALL', codes: [], supported: true, scopeSuffix: '' };
  }
  if (originSel.mode === 'none') {
    return { key: 'NONE', codes: [], supported: false, scopeSuffix: '' };
  }
  const codes = [...(originSel.keys || [])]
    .map((k) => countryKey(k))
    .filter(Boolean)
    .sort();
  const seasonCodes = codes.filter((c) => SEASON_ORIGINS.has(c));
  if (seasonCodes.length === 0) {
    return { key: 'NONE', codes, supported: false, scopeSuffix: '' };
  }
  if (seasonCodes.length === 2) {
    return { key: 'BOTH', codes: seasonCodes, supported: true, scopeSuffix: '' };
  }
  // Exactly one of Peru/Chile — single-origin scope pack.
  return {
    key: seasonCodes[0],
    codes: seasonCodes,
    supported: true,
    scopeSuffix: `__${seasonCodes[0]}`,
  };
}
